import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  CircularProgress,
  Icon,
  InputAdornment,
  makeStyles,
} from "@material-ui/core";

import uverifyLogo from "assets/img/uverify-logo.jpg";

import CustomInput from "components/Forms/CustomInput/CustomInput";

import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

import {
  Container,
  ContentActions,
  ContentForm,
  ContentLogo,
  ResetPasswordButton,
} from "./ResetPasswordForm.styles";
import { useNavigate, useParams } from "react-router-dom";
import { useResetPassword } from "services/input-core/auth/reset-password";
import { toast } from "react-toastify";
import { Button } from "@mui/material";
import useLogin from "hooks/useLogin";

const useStyles = makeStyles(styles as any);

const ResetPasswordForm: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { token } = useParams();
  const { signIn } = useLogin();

  const { t } = useTranslation("login-page");

  const resetPasswordSchema = z.object({
    password: z.string().min(6, { message: t("password-length-error") }),
    confirmPassword: z.string().min(6, { message: t("confirm-password-error") }),
  });

  type ResetPasswordFormFields = z.infer<typeof resetPasswordSchema>;

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ResetPasswordFormFields>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const { mutate: resetPassword, isLoading } = useResetPassword();

  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const onSubmit = async (data: ResetPasswordFormFields) => {
    setLoading(true);

    try {
      if (data.password !== data.confirmPassword) {
        toast.warning(t("passwords-do-not-match"));
        return;
      }

      await resetPasswordSchema.parseAsync(data);
      
      resetPassword(
        {
          password: data.password,
          confirmPassword: data.confirmPassword,
          token: token,
          realm: process.env.REACT_APP_API_AUTH_REALM || "",
        },
        {
          onSuccess: async () => {
            toast.success(t("reset-password-success", { ns: "login-page" }));

            const decodedToken = JSON.parse(atob(token!.split(".")[1]));
            const userName = decodedToken.userName;

            try {
              await signIn(userName, data.password);
            } catch (error) {
              navigate("/auth/login");
            }
          },
          onError: () => {
            toast.error(t("reset-password-error", { ns: "login-page" }));
          },
        }
      );
    } catch (validationError) {
      if (validationError instanceof z.ZodError) {
        validationError.errors.forEach((error) => {
          toast.warning(error.message);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <ContentLogo>
        <img src={uverifyLogo} alt="U-verify Logo" />
      </ContentLogo>

      <form onSubmit={handleSubmit(onSubmit)}>
        <ContentForm>
          <CustomInput
            labelText={t("new-password")}
            id="password"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              ...register("password", { required: true }),
              endAdornment: (
                <InputAdornment position="end" style={{ cursor: "pointer" }}>
                  <Icon
                    className={classes.inputAdornmentIcon}
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? "lock_open" : "lock"}
                  </Icon>
                </InputAdornment>
              ),
              type: showPassword ? "text" : "password",
              autoComplete: "off",
            }}
          />
          {errors.password && (
            <span className="error-message">{errors.password.message}</span>
          )}

          <CustomInput
            labelText={t("confirm-new-password")}
            id="confirm-password"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              ...register("confirmPassword", { required: true }),
              endAdornment: (
                <InputAdornment position="end" style={{ cursor: "pointer" }}>
                  <Icon
                    className={classes.inputAdornmentIcon}
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? "lock_open" : "lock"}
                  </Icon>
                </InputAdornment>
              ),
              type: showPassword ? "text" : "password",
              autoComplete: "off",
            }}
          />
          {errors.confirmPassword && (
            <span className="error-message">
              {errors.confirmPassword.message}
            </span>
          )}
        </ContentForm>

        <ContentActions>
          <ResetPasswordButton
            variant="contained"
            disabled={loading || isLoading}
            type="submit"
          >
            {loading || isLoading ? (
              <CircularProgress size={20} />
            ) : (
              t("confirm")
            )}
          </ResetPasswordButton>
          <Button
            variant="outlined"
            disabled={loading}
            sx={{ width: "100%", marginTop: "14px", padding: "10px" }}
            onClick={() => navigate("/auth/login")}
          >
            {t("back")}
          </Button>
        </ContentActions>
      </form>
    </Container>
  );
};

export default ResetPasswordForm;